import React, { useState, useCallback, useEffect, useRef } from 'react';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import './style.scss';

interface StateProps {
  file: string | undefined | null;
  isBottom?: () => void;
  scale?: number;
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

//pdf埋め込み
const PDFImageRenderViewer: React.FC<StateProps> = (props: StateProps) => {
  const { file, isBottom } = props;

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const renderPDFToImages = async () => {
      try {
        if (!file || !canvasRef.current) return;

        const pdfData = atob(file);
        const pdfArray = new Uint8Array(pdfData.length);
        for (let i = 0; i < pdfData.length; i++) {
          pdfArray[i] = pdfData.charCodeAt(i);
        }
        const pdf = await pdfjs.getDocument(pdfArray).promise;

        const urls = [];
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 2 });

          const canvas = canvasRef.current;
          const context = canvas.getContext('2d')!;
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          // PDFページをCanvasにレンダリング
          await page.render({ canvasContext: context, viewport }).promise;

          urls.push(canvas.toDataURL());
          setImageUrls((prev) => [...prev, canvas.toDataURL()]);

          await sleep(1);
        }

        // ページの画像化ごとにstateを変えると歯抜け(ページそのものは存在するが、真っ白なページが表示される)になるため、
        // 全てが完了した際に再度stateを更新する
        // => sleepで解決かも
        setImageUrls(urls);
        setLoaded(true);
      } catch (err) {
        console.log(err);
      }
    };

    renderPDFToImages();
  }, [file]);

  // 表示領域がコンテンツ全体より大きい場合(=スクロールできない)はscrollイベントが発生しないため、
  // 画像描画後に実測値で判定し、スクロール不可ならisBottomを呼び出す。
  // ページ数や端末ごとの描画サイズ差に左右されないよう、scrollHeightとclientHeightで判定する。
  // 全画像のdecode完了を待ってから測定する(個別のonLoadだと未デコードの兄弟画像が高さ0で計上され誤判定するため)。
  useEffect(() => {
    if (!loaded || !containerRef.current || !isBottom) return;
    let cancelled = false;
    const container = containerRef.current;
    const imgs = Array.from(container.querySelectorAll('img'));
    Promise.all(imgs.map((img) => img.decode().catch(() => undefined))).then(() => {
      if (cancelled) return;
      window.requestAnimationFrame(() => {
        if (cancelled) return;
        const { scrollHeight, clientHeight } = container;
        if (scrollHeight <= clientHeight + 1) {
          isBottom();
        }
      });
    });
    return () => {
      cancelled = true;
    };
  }, [loaded, isBottom]);

  // スクロールイベントのハンドラ
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      if (target.scrollTop >= target.scrollHeight - target.clientHeight - 1 && loaded && isBottom) {
        isBottom();
      }
    },
    [isBottom, loaded],
  );

  return (
    <>
      <div className="pdf-image-viewer">
        <canvas ref={canvasRef}></canvas>
        {imageUrls && imageUrls.length > 0 ? (
          <div ref={containerRef} className="image-container" onScroll={handleScroll}>
            {imageUrls.map((imgUrl, index) => (
              <img key={index} src={imgUrl} alt={`Page ${index + 1}`} />
            ))}
          </div>
        ) : (
          <div className="loading">PDFファイルを読み込み中です</div>
        )}
      </div>
    </>
  );
};
export default PDFImageRenderViewer;
